import { useEffect, useState, type ReactNode } from 'react'
import { talkUiLng } from '@/utils/talkWelt'
import { useTalkT } from '@/talk/useTalkT'
import { HelpSupportBadge } from './HelpSupportBadge'
import { HelpZettelStatus } from './HelpZettelStatus'
import { ROLE_LABELS, type Role } from '@/types/role'
import type { HelpPair as HelpPairModel, HelpZettelStatus as ZettelStatus } from '@/types/help'
import { dropHelpAudioCache, fetchHelpAudioUrl, isTalkPlaying, playTalkUrl } from '@/talk/helpAudio'
import { formatScopeLabel, formatTalkDuration, formatTalkTime, isTranscriptPending } from '@/talk/talkFormat'
import { TranscriptRetryButton } from '@/talk/TranscriptRetryButton'

export function HelpPair({
  pair,
  conversationId,
  light = false,
  localPlayUrl,
  onSupportReply,
  onDone,
  showSupportActions = false,
  allowServerPlay = false,
  status,
  composer,
}: {
  pair: HelpPairModel
  conversationId: string
  light?: boolean
  localPlayUrl?: string | null
  onSupportReply?: (zettelId: string) => void
  onDone?: (zettelId: string) => void
  showSupportActions?: boolean
  allowServerPlay?: boolean
  status?: ZettelStatus | string
  composer?: ReactNode
}) {
  const t = useTalkT()
  const lng = talkUiLng()
  const [serverUrl, setServerUrl] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const zettelStatus = (status ?? pair.status) as ZettelStatus
  const roleLabel = ROLE_LABELS[pair.sender_role as Role] ?? pair.sender_role
  const scopes = pair.opener.sender_scopes ?? []
  const playUrl = localPlayUrl || (pair.opener.has_audio ? serverUrl : null)
  const wantsServer = (allowServerPlay || showSupportActions) && pair.opener.has_audio && !localPlayUrl
  const showPlay = Boolean(playUrl)

  useEffect(() => {
    if (!wantsServer) {
      setServerUrl(null)
      dropHelpAudioCache(conversationId, pair.opener.id)
      return
    }
    let live = true
    void fetchHelpAudioUrl(conversationId, pair.opener.id).then(url => {
      if (live) setServerUrl(url)
    })
    return () => { live = false }
  }, [wantsServer, conversationId, pair.opener.id])

  const pending = isTranscriptPending(pair.opener.transcript_pending, pair.opener.body, pair.opener.has_audio)
  const body = pending
    ? t('talk_transcript_pending')
    : pair.opener.body
  const duration = formatTalkDuration(pair.opener.audio_duration_ms)
  const who = pair.opener.sender_name || roleLabel
  const time = formatTalkTime(pair.opener.created_at)

  return (
    <article className={light ? 'help-pair' : 'talk-pair'}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="role">{roleLabel}</div>
          {scopes.length > 0 && (
            <div className="scopes">{scopes.map(key => formatScopeLabel(key, lng)).join(' · ')}</div>
          )}
        </div>
        <HelpZettelStatus status={zettelStatus} light={light} />
      </div>
      <div className="text-sm whitespace-pre-wrap break-words">{body}</div>
      <TranscriptRetryButton conversationId={conversationId} zettelId={pair.zettel_id} pending={pending} />
      <div className="talk-meta">
        <span>{[who, time].filter(Boolean).join(' · ')}</span>
        {showPlay && playUrl && (
          <button
            type="button"
            className="talk-play"
            aria-pressed={playing}
            aria-label={t('talk_play')}
            onClick={() => {
              const was = isTalkPlaying(pair.opener.id)
              playTalkUrl(pair.opener.id, playUrl, () => setPlaying(false))
              setPlaying(!was)
            }}
          >
            <i>{playing ? '❚❚' : '▶'}</i>
            {duration || null}
          </button>
        )}
      </div>
      {pair.replies.filter(r => (r.body || '').trim()).map(r => (
        <div key={r.id} className="ans">
          <div className="talk-meta">
            <HelpSupportBadge compact />
            <span>{formatTalkTime(r.created_at)}</span>
          </div>
          <div className="mt-1 text-sm whitespace-pre-wrap break-words">{r.body}</div>
        </div>
      ))}
      {showSupportActions && zettelStatus !== 'erledigt' && zettelStatus !== 'deactivated' && (
        <div className="mt-2 flex gap-2">
          {onSupportReply && (
            <button type="button" className="text-[12px] font-medium px-2.5 py-1 rounded-lg border border-gray-200 bg-white" onClick={() => onSupportReply(pair.zettel_id)}>
              {t('help_answers')}
            </button>
          )}
          {onDone && (
            <button type="button" className="text-[12px] font-medium px-2.5 py-1 rounded-lg border border-gray-200 bg-white" onClick={() => onDone(pair.zettel_id)}>
              {t('talk_done')}
            </button>
          )}
        </div>
      )}
      {composer}
    </article>
  )
}
